import { memo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { K } from '../apiServiceI18n';
import {
  TIMEFRAME_1D,
  TIMEFRAME_1M,
  TIMEFRAME_1W,
  TIMEFRAME_CUSTOM,
  clampQuantity,
  formatDateTimeDisplay,
  rangeForPreset,
} from './apiServicePerformanceUtils';

const tabBtnSx = (active) => ({
  minWidth: 44,
  px: 1.5,
  py: 0.5,
  fontWeight: 700,
  fontSize: '0.8rem',
  textTransform: 'none',
  borderRadius: 1,
  bgcolor: active ? '#3d4654' : '#fff',
  color: active ? '#fff' : '#5f6878',
  border: '1px solid #c5cad3',
  '&:hover': { bgcolor: active ? '#3d4654' : '#f3f4f6' },
});

function copyToClipboard(text) {
  if (text) navigator.clipboard.writeText(text).catch(() => {});
}

function UrlField({ label, value, copyTooltip }) {
  if (!value) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        minWidth: 0,
        flex: '1 1 200px',
        maxWidth: 360,
      }}
    >
      <TextField
        label={label}
        value={value}
        size="small"
        fullWidth
        InputProps={{ readOnly: true }}
        sx={{
          minWidth: 0,
          '& .MuiInputBase-input': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />
      <Tooltip title={copyTooltip}>
        <IconButton size="small" onClick={() => copyToClipboard(value)} sx={{ mt: 0.5, flexShrink: 0 }}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function ApiServicePerformanceSingularToolbar({
  timeframe,
  onTimeframeChange,
  customRange,
  onCustomRangeChange,
  quantity,
  onQuantityChange,
  baseUrl,
  customUrl,
}) {
  const { t } = useTranslation();
  const [dateAnchor, setDateAnchor] = useState(null);
  const [draftFromDate, setDraftFromDate] = useState(customRange.from);
  const [draftToDate, setDraftToDate] = useState(customRange.to);
  const [draftFromTime, setDraftFromTime] = useState(formatDateTimeDisplay(customRange.from).slice(11));
  const [draftToTime, setDraftToTime] = useState(formatDateTimeDisplay(customRange.to).slice(11));
  const [qtyDraft, setQtyDraft] = useState(String(quantity));

  const applyDateRange = () => {
    const parseTime = (date, timeStr, fallbackHour, fallbackMin) => {
      const base = dayjs(date);
      const match = String(timeStr || '').match(/^(\d{1,2}):(\d{2})$/);
      if (match) return base.hour(Number(match[1])).minute(Number(match[2])).second(0);
      return base.hour(fallbackHour).minute(fallbackMin).second(0);
    };
    onCustomRangeChange({
      from: parseTime(draftFromDate, draftFromTime, 0, 0),
      to: parseTime(draftToDate, draftToTime, 23, 59),
    });
    onTimeframeChange(TIMEFRAME_CUSTOM);
    setDateAnchor(null);
  };

  const commitQuantity = (raw) => {
    const next = clampQuantity(raw);
    setQtyDraft(String(next));
    onQuantityChange(next);
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
      <Stack direction="row" spacing={0.75}>
        {[TIMEFRAME_1M, TIMEFRAME_1W, TIMEFRAME_1D].map((tf) => (
          <Button
            key={tf}
            onClick={() => {
              onTimeframeChange(tf);
              onCustomRangeChange(rangeForPreset(tf));
            }}
            sx={tabBtnSx(timeframe === tf)}
          >
            {tf}
          </Button>
        ))}
      </Stack>

      <Tooltip title={t(K.API_SVC_PERF_DATE_RANGE_TOOLTIP)}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CalendarMonthIcon />}
          onClick={(e) => {
            setDraftFromDate(customRange.from);
            setDraftToDate(customRange.to);
            setDraftFromTime(formatDateTimeDisplay(customRange.from).slice(11));
            setDraftToTime(formatDateTimeDisplay(customRange.to).slice(11));
            setDateAnchor(e.currentTarget);
          }}
          sx={{ textTransform: 'none', borderColor: '#c5cad3', color: '#5f6878' }}
        >
          {formatDateTimeDisplay(customRange.from)} – {formatDateTimeDisplay(customRange.to).slice(11)}
        </Button>
      </Tooltip>

      {(baseUrl || customUrl) && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ flex: '1 1 auto', minWidth: 0, flexWrap: 'wrap', alignItems: 'flex-start' }}
        >
          <UrlField
            label={t(K.API_SVC_BASE_URL)}
            value={baseUrl}
            copyTooltip={t(K.API_SVC_COPY_BASE_URL)}
          />
          <UrlField
            label={t(K.API_SVC_CUSTOM_URL)}
            value={customUrl}
            copyTooltip={t(K.API_SVC_COPY_CUSTOM_URL)}
          />
        </Stack>
      )}

      <Box sx={{ flex: 1 }} />

      <TextField
        size="small"
        type="number"
        label={t(K.API_SVC_PERF_QUANTITY)}
        value={qtyDraft}
        inputProps={{ min: 10, max: 99 }}
        sx={{ width: 100 }}
        onChange={(e) => setQtyDraft(e.target.value)}
        onBlur={(e) => commitQuantity(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitQuantity(e.target.value);
        }}
      />

      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 560 }}>
          <Stack direction="row" spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar value={dayjs(draftFromDate)} onChange={(d) => setDraftFromDate(d)} />
              <DateCalendar value={dayjs(draftToDate)} onChange={(d) => setDraftToDate(d)} />
            </LocalizationProvider>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label={t(K.LBL_FROM)}
              value={`${formatDateTimeDisplay(draftFromDate).slice(0, 10)} ${draftFromTime}`}
              onChange={(e) => {
                const parts = e.target.value.trim().split(/\s+/);
                if (parts[1]) setDraftFromTime(parts[1]);
              }}
              fullWidth
            />
            <TextField
              size="small"
              label={t(K.LBL_TO)}
              value={`${formatDateTimeDisplay(draftToDate).slice(0, 10)} ${draftToTime}`}
              onChange={(e) => {
                const parts = e.target.value.trim().split(/\s+/);
                if (parts[1]) setDraftToTime(parts[1]);
              }}
              fullWidth
            />
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" onClick={applyDateRange} sx={{ bgcolor: '#3d4654' }}>
              {t(K.BTN_APPLY)}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

export default memo(ApiServicePerformanceSingularToolbar);
